import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Loader2, KeyRound, LogOut } from "lucide-react";

export default function ChangePassword() {
  const { refresh, logout, user } = useAuth();
  const nav = useNavigate();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (newPw.length < 6) return setErr("New password must be at least 6 characters");
    if (newPw !== confirm) return setErr("Passwords do not match");
    if (newPw === oldPw) return setErr("New password must be different from current");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Password updated");
      await refresh();
      nav("/", { replace: true });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen blueprint-grid flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <div className="h-10 w-10 border border-amber-500/60 bg-amber-500/10 flex items-center justify-center">
            <KeyRound className="h-5 w-5 text-amber-400" />
          </div>
          <div>
            <div className="text-lg font-semibold">Set Your Password</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
              Required before continuing · {user?.username}
            </div>
          </div>
        </div>
        <form onSubmit={submit} className="border border-zinc-800 bg-zinc-950/80 p-6 space-y-4" data-testid="change-password-form">
          <div>
            <label className="text-xs font-mono uppercase tracking-[0.1em] text-zinc-400 block mb-2">Current password</label>
            <input type="password" autoFocus value={oldPw} onChange={(e) => setOldPw(e.target.value)} data-testid="change-old-password"
              className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 focus:ring-2 focus:ring-red-500/30 rounded-sm px-3 py-2 font-mono text-sm outline-none transition-colors" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-[0.1em] text-zinc-400 block mb-2">New password</label>
            <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="change-new-password"
              className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 focus:ring-2 focus:ring-red-500/30 rounded-sm px-3 py-2 font-mono text-sm outline-none transition-colors" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-[0.1em] text-zinc-400 block mb-2">Confirm new password</label>
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="change-confirm-password"
              className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 focus:ring-2 focus:ring-red-500/30 rounded-sm px-3 py-2 font-mono text-sm outline-none transition-colors" />
          </div>
          {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 px-3 py-2 rounded-sm" data-testid="change-password-error">{err}</div>}
          <button type="submit" disabled={busy} data-testid="change-password-submit"
            className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-medium py-2 rounded-sm transition-colors flex items-center justify-center gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update Password
          </button>
          <button type="button" onClick={logout} data-testid="change-password-logout"
            className="w-full border border-zinc-800 hover:bg-zinc-900 text-zinc-400 text-sm py-2 rounded-sm transition-colors flex items-center justify-center gap-2">
            <LogOut className="h-4 w-4" /> Sign out instead
          </button>
        </form>
      </div>
    </div>
  );
}
